import './Story.sass';

import React, { FC, MouseEvent, TouchEvent, useCallback, useMemo } from 'react';
import { StoryProps } from './Story.interface';
import { StoryPlayer, StoryProgress, UnitName, UnitPhoto } from '$shared/components';
import {
  useCurrentUserInfo,
  useDateShort,
  useDesktop,
  useThumbHash,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { useSpring } from '@react-spring/web';
import { IcClose28, IcDelete24, IcSoundOff24, IcSoundOn24, IcView20 } from '$assets';
import { goTo, hideModal, showModal } from '$navigation/helper';
import { Button } from '$uikit';
import { CHAT, PROFILE, RESOLVE } from '$shared/constants/pages';
import { useDispatch, useSelector } from 'react-redux';
import { storiesActions, storiesSoundSelector } from '$store/stories';
import { _confirm } from '$utils';

const StoryComponent: FC<StoryProps> = ({ userId, id, stories, onEnd, simple }) => {
  const dispatch = useDispatch();
  const user = useUserInfo(userId);
  const curUser = useCurrentUserInfo();
  const t = useTranslator();
  const [{ progress }, api] = useSpring(() => ({ progress: 0 }));
  const sound = useSelector(storiesSoundSelector);
  const isDesktop = useDesktop();

  const handleSoundToggle = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      // const video = document.querySelector('.StoryPlayer__video video') as HTMLVideoElement;
      // if (video) video.muted = sound;
      dispatch(storiesActions.toggleSound());
    },
    [sound, dispatch],
  );

  const storyId = useMemo(() => {
    return id || stories?.[0].id;
  }, [stories, id]);

  const index = stories?.findIndex((s) => s.id === storyId);
  const story = stories[index];
  const storyPlaceholder = useThumbHash(story.thumbHash, story.thumbnail);
  const date = useDateShort(story.createdAt);

  const handleUserClick = useCallback(() => {
    goTo(RESOLVE, { domain: user.domain || `id${user.id}` });
  }, [user]);

  const handleClose = useCallback((e: TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopPropagation();
    e.nativeEvent.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
    queueMicrotask(() => {
      hideModal();
    });
  }, []);

  const handleViews = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      showModal(`storyViews${storyId}`);
    },
    [storyId],
  );

  const handleDelete = (e: TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopPropagation();
    e.nativeEvent.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
    queueMicrotask(() => {
      _confirm(t('stories_delete_confirm'), () =>
        dispatch(storiesActions.deleteStory(storyId)),
      );
    });
  };

  const handleOpenChat = useCallback((peerId: number) => {
    goTo(CHAT, { peerId });
  }, []);

  const handleOpenProfile = useCallback((id: number) => {
    goTo(PROFILE, { id });
  }, []);

  const getHandlers = (func: Function) => {
    if (isDesktop) {
      return {
        onPointerDown: func,
      };
    } else {
      return {
        onPointerDown: (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopPropagation();
          e.nativeEvent.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        },
        onTouchEnd: func,
      };
    }
  };

  const actionButton = useMemo(() => {
    if (story.actionButton) {
      if (story.actionButton.action === 'chat') {
        return (
          <Button
            className="Story__openChat"
            stretched
            onPointerDown={() => handleOpenChat(+story.actionButton!.payload)}
          >
            {t('stories_open_chat')}
          </Button>
        );
      } else if (story.actionButton.action === 'profile') {
        return (
          <Button
            className="Story__openChat"
            stretched
            onPointerDown={() => handleOpenProfile(+story.actionButton!.payload)}
          >
            {t('stories_open_profile')}
          </Button>
        );
      }
    }

    return (
      <Button
        className="Story__openChat"
        stretched
        onPointerDown={() => handleOpenChat(story.userId)}
      >
        {t('stories_open_chat')}
      </Button>
    );
  }, [t, story.userId, story.actionButton]);

  return (
    <div className="Story">
      {!simple ? (
        <StoryPlayer
          key={storyId}
          onEnd={onEnd}
          story={story}
          muted={!sound}
          animation={api}
        />
      ) : (
        <div
          className="Story__empty"
          style={{ backgroundImage: `url(${storyPlaceholder})` }}
        />
      )}
      <div className="Story__top">
        {!simple && (
          <StoryProgress
            key={storyId}
            total={stories.length}
            current={index}
            animation={progress}
          />
        )}
        <div className="Story__header">
          <div onPointerDown={handleUserClick} className="Story__header__left">
            <div className="Story__header__avatar">
              <UnitPhoto
                size="extra-small"
                unit={user}
                nftIndicator={false}
                onlineIndicator={false}
              />
            </div>
            <div className="Story__header__content">
              <div className="Story__header__content__userName">
                <UnitName unit={user} />
              </div>
              <div className="Story__header__content__date">{date}</div>
            </div>
          </div>
          {!simple && (
            <div className="Story__actions">
              {story.type === 'video' && (
                <div
                  onPointerDown={handleSoundToggle}
                  className="Story__actions__item Story__sound"
                >
                  {sound ? <IcSoundOn24 /> : <IcSoundOff24 />}
                </div>
              )}
              {/*@ts-ignore*/}
              <div
                {...getHandlers(handleClose)}
                className="Story__actions__item Story__close"
              >
                <IcClose28 />
              </div>
            </div>
          )}
        </div>
      </div>
      {!simple && (
        <div className="Story__bottom">
          <div className="Story__footer">
            {user.id === curUser.id && (
              <div className="Story__views" onPointerDown={handleViews}>
                <IcView20 />
                <span className="Story__views__count">{story.views || 0}</span>
              </div>
            )}
            {(user.verified === 2 || user.id === 5) &&
              user.id != curUser.id &&
              actionButton}
            {userId === curUser.id && (
              // @ts-ignore
              <Button {...getHandlers(handleDelete)} className="Story__delete">
                <IcDelete24 />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const Story = React.memo(StoryComponent);
